import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faPlus, faEdit, faCheck, faUserCog, faCog, faInfoCircle, faClone } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../../utils/apiConfig';
import AccountUserManagement from './AccountUserManagement';
import AccountSettingsManagement from './AccountSettingsManagement';

function AccountManagement({ userId, refreshTrigger, onRefresh }) {
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active'
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'descending'
  });

  // Account management state
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showSettingsManagement, setShowSettingsManagement] = useState(false);

  // Duplicate-account state
  const [duplicatingAccount, setDuplicatingAccount] = useState(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);

  // Add a ref to track if we're currently fetching accounts
  const isFetchingAccountsRef = useRef(false);

  // Memoize the fetchAccounts function to prevent unnecessary re-creation
  const fetchAccounts = useCallback(async () => {
    // If we're already fetching accounts, don't start another fetch
    if (isFetchingAccountsRef.current) {
      return;
    }

    try {
      // Set the fetching flag to prevent duplicate API calls
      isFetchingAccountsRef.current = true;
      setIsLoading(true);

      // First try to get accounts where the user is an admin
      try {
        const accountAdminResponse = await api.get(getApiUrl('/api/account-admin/accounts'), {
          params: { userId }
        });

        if (accountAdminResponse.data && accountAdminResponse.data.length > 0) {
          setAccounts(accountAdminResponse.data);
          setIsLoading(false);
          isFetchingAccountsRef.current = false;
          return;
        }
      } catch (err) {
        // If this fails, the user might be a super admin, so we'll try the regular admin endpoint
        console.log('Not an account admin, trying super admin endpoint');
      }

      // If the user is not an account admin or has no accounts, try the admin endpoint
      const response = await api.get(getApiUrl('/api/admin/accounts'), {
        params: { userId }
      });
      setAccounts(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setError('Failed to load accounts. Please try again later.');
      setIsLoading(false);
    } finally {
      // Reset the fetching flag
      isFetchingAccountsRef.current = false;
    }
  }, [userId]);

  // Fetch accounts when the component mounts or refreshTrigger changes
  useEffect(() => {
    // Use a small timeout to debounce multiple rapid calls
    const debounceTimeout = setTimeout(() => {
      fetchAccounts();
    }, 50);

    // Clean up the timeout on unmount or when dependencies change
    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [fetchAccounts, refreshTrigger]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleAddAccount = () => {
    setEditingAccount(null);
    setFormData({
      name: '',
      description: '',
      status: 'active'
    });
    setShowModal(true);
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      description: account.description || '',
      status: account.status || 'active'
    });
    setShowModal(true);
  };

  const handleDuplicateAccount = (account) => {
    setDuplicatingAccount(account);
    setDuplicateName(`${account.name} (Copy)`);
    setDuplicateError(null);
    setShowModal(false);
  };

  const handleConfirmDuplicate = async () => {
    if (!duplicatingAccount || isDuplicating) return;

    setIsDuplicating(true);
    setDuplicateError(null);

    try {
      const response = await api.post(
        getApiUrl(`/api/admin/accounts/${duplicatingAccount.id}/duplicate`),
        { name: duplicateName.trim(), userId }
      );

      setAccounts([...accounts, response.data]);
      setDuplicatingAccount(null);

      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error duplicating account:', error);
      setDuplicateError(
        'Failed to duplicate account: ' + (error.response?.data?.error || error.message)
      );
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleManageUsers = (account) => {
    setSelectedAccount(account);
    setShowUserManagement(true);
    setShowSettingsManagement(false);
  };

  const handleManageSettings = (account) => {
    setSelectedAccount(account);
    setShowSettingsManagement(true);
    setShowUserManagement(false);
  };

  const handleBackToAccounts = () => {
    setSelectedAccount(null);
    setShowUserManagement(false);
    setShowSettingsManagement(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingAccount) {
        // Update existing account
        const response = await api.put(getApiUrl(`/api/admin/accounts/${editingAccount.id}`), {
          ...formData,
          userId
        });

        // Update local state
        setAccounts(accounts.map(account =>
          account.id === editingAccount.id ? { ...account, ...response.data } : account
        ));
      } else {
        // Create new account
        const response = await api.post(getApiUrl('/api/admin/accounts'), {
          ...formData,
          userId
        });

        // Update local state
        setAccounts([...accounts, response.data]);
      }

      setShowModal(false);

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error saving account:', error);
      setError('Failed to save account: ' + (error.response?.data?.error || error.message));
    }
  };

  // Handle sorting when a column header is clicked
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Get the sorted data
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      // Handle null or undefined values
      if (a[sortConfig.key] == null) return sortConfig.direction === 'ascending' ? 1 : -1;
      if (b[sortConfig.key] == null) return sortConfig.direction === 'ascending' ? -1 : 1;

      // Handle date fields
      if (sortConfig.key === 'created_at' || sortConfig.key === 'updated_at') {
        const dateA = a[sortConfig.key] ? new Date(a[sortConfig.key]) : new Date(0);
        const dateB = b[sortConfig.key] ? new Date(b[sortConfig.key]) : new Date(0);
        return sortConfig.direction === 'ascending' ? dateA - dateB : dateB - dateA;
      }

      // Handle string fields
      if (typeof a[sortConfig.key] === 'string' && typeof b[sortConfig.key] === 'string') {
        return sortConfig.direction === 'ascending'
          ? a[sortConfig.key].localeCompare(b[sortConfig.key])
          : b[sortConfig.key].localeCompare(a[sortConfig.key]);
      }

      // Handle other fields
      return sortConfig.direction === 'ascending'
        ? a[sortConfig.key] > b[sortConfig.key] ? 1 : -1
        : a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
    });
  };

  // Get sort direction indicator
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
  };

  // Apply sorting to accounts
  const sortedAccounts = getSortedData(accounts);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          {/* <div className="loading-spinner"></div> */}
          <p>Loading accounts...</p>
        </div>
      </div>
    );
  }

  if (error && !showModal) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchAccounts}>Retry</button>
        </div>
      </div>
    );
  }

  // If we're showing user management for a specific account
  if (showUserManagement && selectedAccount) {
    return (
      <div className="admin-card">
        <div className="admin-card-header">
          <button
            className="btn btn-secondary"
            onClick={handleBackToAccounts}
          >
            &larr; Back to Accounts
          </button>
        </div>

        <AccountUserManagement
          userId={userId}
          accountId={selectedAccount.id}
          accountName={selectedAccount.name}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  // If we're showing settings management for a specific account
  if (showSettingsManagement && selectedAccount) {
    return (
      <div className="admin-card">
        <div className="admin-card-header">
          <button
            className="btn btn-secondary"
            onClick={handleBackToAccounts}
          >
            &larr; Back to Accounts
          </button>
        </div>

        <AccountSettingsManagement
          userId={userId}
          accountId={selectedAccount.id}
          accountName={selectedAccount.name}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  // Otherwise show the account management view
  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <h2 className="admin-card-title">
          <FontAwesomeIcon icon={faBuilding} className="icon" />
          Account Management
        </h2>
        <button
          className="btn btn-primary"
          onClick={handleAddAccount}
        >
          <FontAwesomeIcon icon={faPlus} /> Add Account
        </button>
      </div>

      {error && showModal && (
        <div className="alert alert-danger">{error}</div>
      )}

      {/* Account Form Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{editingAccount ? 'Edit Account' : 'Add New Account'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Account Name</label>
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  className="form-control"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  className="form-control"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {!editingAccount && (
                <div className="alert alert-info d-flex align-items-center mt-3">
                  <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                  <span>You will automatically be added as an account admin for this new account.</span>
                </div>
              )}

              <div className="modal-actions">
                <button type="submit" className="btn btn-success">
                  <FontAwesomeIcon icon={faCheck} /> {editingAccount ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Duplicate Account Modal */}
      {duplicatingAccount && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Duplicate Account</h3>
            <p>
              Creating a copy of <strong>{duplicatingAccount.name}</strong>.
            </p>

            {duplicateError && (
              <div className="alert alert-danger">{duplicateError}</div>
            )}

            <div className="form-group">
              <label htmlFor="duplicate-account-name">New Account Name</label>
              <input
                id="duplicate-account-name"
                type="text"
                className="form-control"
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
                disabled={isDuplicating}
                required
              />
            </div>

            <div className="alert alert-info mt-3">
              <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
              <strong>Carried over:</strong> account settings, account admins and
              members (with their admin flags), and channel configuration mappings.
              <br />
              <strong>Not carried over:</strong> folders and campaigns (and anything
              tied to them, such as schedules, pacing, and QA jobs), scheduled cron
              jobs, and OAuth connections &mdash; the new account must be
              re-authorized.
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-success"
                onClick={handleConfirmDuplicate}
                disabled={isDuplicating || !duplicateName.trim()}
              >
                <FontAwesomeIcon icon={faClone} />{' '}
                {isDuplicating ? 'Duplicating...' : 'Duplicate'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setDuplicatingAccount(null)}
                disabled={isDuplicating}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {accounts.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faBuilding} className="empty-state-icon" />
          <p className="empty-state-text">No accounts defined yet</p>
          <button
            className="btn btn-primary"
            onClick={handleAddAccount}
          >
            <FontAwesomeIcon icon={faPlus} /> Add First Account
          </button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th onClick={() => requestSort('name')} className="sortable-header">
                  Name{getSortDirectionIndicator('name')}
                </th>
                <th onClick={() => requestSort('description')} className="sortable-header">
                  Description{getSortDirectionIndicator('description')}
                </th>
                <th onClick={() => requestSort('created_by_name')} className="sortable-header">
                  Created By{getSortDirectionIndicator('created_by_name')}
                </th>
                <th onClick={() => requestSort('created_at')} className="sortable-header">
                  Created On{getSortDirectionIndicator('created_at')}
                </th>
                <th onClick={() => requestSort('updated_at')} className="sortable-header">
                  Last Updated{getSortDirectionIndicator('updated_at')}
                </th>
                <th onClick={() => requestSort('status')} className="sortable-header">
                  Status{getSortDirectionIndicator('status')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedAccounts.map(account => (
                <tr key={account.id}>
                  <td>{account.name}</td>
                  <td>{account.description || '-'}</td>
                  <td>{account.created_by_name || '-'}</td>
                  <td>{formatDate(account.created_at)}</td>
                  <td>{formatDate(account.updated_at)}</td>
                  <td>
                    <span className={`badge badge-${account.status === 'active' ? 'success' : 'secondary'}`}>
                      {account.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleManageUsers(account)}
                        title="Manage Account Admins"
                      >
                        <FontAwesomeIcon icon={faUserCog} />
                      </button>
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => handleManageSettings(account)}
                        title="Manage Settings"
                      >
                        <FontAwesomeIcon icon={faCog} />
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEditAccount(account)}
                        title="Edit Account"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => handleDuplicateAccount(account)}
                        title="Duplicate Account"
                      >
                        <FontAwesomeIcon icon={faClone} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AccountManagement;
